<template lang="html">
    <vs-popup :active.sync="$props.popupActive" title="Mote-clé catégories & sous-catégories" :style="{ zIndex: $props.zIndex }">
        <div class="keyword-categories-container">
            <!-- Categories Section -->
            <div class="category-section">
                <h3 class="section-title">Categories</h3>
                <div class="flex items-center space-x-5 mb-4">
                    <vs-input
                        v-model="newCategory"
                        placeholder="Ajouter une nouvelle catégorie"
                        @keyup.enter="addCategory"
                    />
                    <vs-button @click="addCategory">Ajouter</vs-button>
                </div>
                <vs-table
                    pagination
                    max-items="15"
                    search
                    :data="keywordCategories"
                >
                    <template slot="thead">
                        <vs-th sort-key="name">Catégorie</vs-th>
                        <vs-th>Sous-catégories</vs-th>
                        <vs-th>Actions</vs-th>
                    </template>
                    <template slot-scope="{ data }">
	<vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
		<vs-td :data="data[indextr].name">
			<div v-if="data[indextr].isEditing">
				<input
					v-model="data[indextr].editName"
					class="edit-input"
					@keyup.enter="saveEdit(data[indextr])"
				/>
			</div>
			<div v-else>
				{{ data[indextr].name }}
			</div>
		</vs-td>
		<vs-td>
			<!-- Subcategories display and management -->
			<div class="subcategory-management">
				<!-- Normal mode: just show subcategories as chips -->
				<div v-if="!data[indextr].isEditing" class="subcategories-chips">
					<vs-chip
						v-for="subcategory in getSubcategoriesForCategory(data[indextr].id)"
						:key="subcategory.id"
						color="primary"
					>
						{{ subcategory.name }}
					</vs-chip>
				</div>

				<!-- Edit mode: show delete options and add new subcategory -->
				<div v-else>
					<!-- Existing subcategories with delete icons -->
					<div class="subcategories-chips">
						<div
							v-for="subcategory in getSubcategoriesForCategory(
								data[indextr].id
							)"
							:key="subcategory.id"
							class="subcategory-chip-wrapper"
						>
							<vs-chip color="primary">
								{{ subcategory.name }}
							</vs-chip>
							<vs-icon
								icon="clear"
								size="small"
								color="danger"
								class="delete-subcategory-icon"
								@click.stop="onRemoveSubcategoryClick(subcategory.id)"
							></vs-icon>
						</div>
					</div>

					<!-- Add new subcategory inline -->
					<div class="add-subcategory-inline">
						<input
							v-model="data[indextr].newSubcategoryName"
							placeholder="Nouvelle sous-catégorie"
							class="subcategory-input"
							@keyup.enter="addSubcategoryForCategory(data[indextr])"
						/>
						<vs-button
							size="small"
							type="border"
							@click="addSubcategoryForCategory(data[indextr])"
						>
							Ajouter
						</vs-button>
					</div>
				</div>
			</div>
		</vs-td>
		<vs-td class="flex-actions">
			<vs-icon
				v-if="data[indextr].isEditing"
				icon="save"
				size="small"
				color="success"
				class="action-icon"
				@click.stop="saveEdit(data[indextr])"
			></vs-icon>
			<vs-icon
				v-else
				icon="edit"
				size="small"
				color="primary"
				class="action-icon"
				@click.stop="startEdit(data[indextr])"
			></vs-icon>
			<vs-icon
				icon="delete"
				size="small"
				color="danger"
				class="action-icon"
				@click.stop="onRemoveClick(data[indextr].id)"
			></vs-icon>
		</vs-td>
	</vs-tr>
</template>
                </vs-table>
            </div>
        </div>
    </vs-popup>
</template>
    
<script>
import dbFieldConvertor from "@/mixins/dbFieldConvertorMixin";
import utilsMixin from "@/mixins/utilsMixin";
import Keywords from "../../entities/Keywords";
import store from "@/store/store";

export default {
	created() {
		this.getKeywordsCategories();
		this.getKeywordsSubcategories();
	},
	components: {},
	mounted() {},
	name: "KeywordCategoriesPopup",
	mixins: [dbFieldConvertor, utilsMixin],
	props: {
		zIndex: {
			type: Number,
			default: 1000,
		},
		popupActive: Boolean,
	},

	data() {
		return {
			newCategory: "",
			subcategories: [], // Initialize with empty array to avoid undefined errors
		};
	},
	computed: {
		keywordCategories() {
			return (store.state.keywordsCategories || []).map((category) => ({
				...category,
				isEditing: false,
				editName: "",
				newSubcategoryName: "", // For adding new subcategories inline
			}));
		},
	},
	methods: {
		async getKeywordsCategories() {
			try {
				await store.dispatch("fetchKeywordCategories");
			} catch (error) {
				this.$vs.notify({
					title: "Erreur",
					text: "Erreur lors de la récupération des catégories de mots-clés.",
					color: "danger",
				});
				console.error("Error fetching keywords categories:", error);
			}
		},
		async getKeywordsSubcategories() {
			try {
				// Check if the method exists before calling it
				const response = await Keywords.getKeywordsSubcategories();
				this.subcategories = response.reverse();
			} catch (error) {
				this.subcategories = []; // Initialize with empty array on error
				this.$vs.notify({
					title: "Erreur",
					text: "Erreur lors de la récupération des sous-catégories.",
					color: "danger",
				});
				console.error("Error fetching subcategories:", error);
			}
		},
		async addCategory() {
			try {
				if (this.newCategory.trim() === "") {
					this.$vs.notify({
						title: "Erreur",
						text: "Le nom de la catégorie ne peut pas être vide.",
						color: "danger",
					});
					return;
				}
				await Keywords.addKeywordsCategory({ name: this.newCategory });
				this.newCategory = "";
				this.getKeywordsCategories();

				this.$vs.notify({
					title: "Succès",
					text: "Catégorie ajoutée avec succès.",
					color: "success",
				});
			} catch (error) {
				this.$vs.notify({
					title: "Erreur",
					text: "Erreur lors de l'ajout de la catégorie.",
					color: "danger",
				});
				console.error("Error adding keyword category:", error);
			}
		},
		async addSubcategoryForCategory(category) {
			try {
				const subcategoryName = category.newSubcategoryName.trim();

				if (!subcategoryName) {
					this.$vs.notify({
						title: "Erreur",
						text: "Le nom de la sous-catégorie ne peut pas être vide.",
						color: "danger",
					});
					return;
				}

				const payload = {
					name: subcategoryName,
					category_id: category.id,
				};

				await Keywords.addKeywordsSubcategory(payload);

				// Reset the input field
				this.$set(category, "newSubcategoryName", "");

				// Refresh subcategories
				this.getKeywordsSubcategories();

				this.$vs.notify({
					title: "Succès",
					text: "Sous-catégorie ajoutée avec succès.",
					color: "success",
				});
			} catch (error) {
				this.$vs.notify({
					title: "Erreur",
					text: "Erreur lors de l'ajout de la sous-catégorie.",
					color: "danger",
				});
				console.error("Error adding subcategory:", error);
			}
		},
		async onRemoveClick(id) {
			try {
				await Keywords.delKeywordsCategories(id);
				this.getKeywordsCategories();
				// Refresh subcategories as well since some might be linked to this category
				this.getKeywordsSubcategories();

				this.$vs.notify({
					title: "Succès",
					text: "Catégorie supprimée avec succès.",
					color: "success",
				});
			} catch (error) {
				this.$vs.notify({
					title: "Erreur",
					text: "Erreur lors de la suppression de la catégorie.",
					color: "danger",
				});
				console.error("Error removing keyword category:", error);
			}
		},
		async onRemoveSubcategoryClick(id) {
			try {
				await Keywords.delKeywordsSubcategory(id);
				this.getKeywordsSubcategories();

				this.$vs.notify({
					title: "Succès",
					text: "Sous-catégorie supprimée avec succès.",
					color: "success",
				});
			} catch (error) {
				this.$vs.notify({
					title: "Erreur",
					text: "Erreur lors de la suppression de la sous-catégorie.",
					color: "danger",
				});
				console.error("Error removing subcategory:", error);
			}
		},
		startEdit(category) {
			category.isEditing = true;
			category.editName = category.name;
		},
		async saveEdit(category) {
			try {
				const trimmedName = category.editName.trim();
				if (!trimmedName) {
					this.$vs.notify({
						title: "Erreur",
						text: "Le nom de la catégorie ne peut pas être vide",
						color: "warning",
					});
					return;
				}

				await Keywords.updateKeywordCategory(
					{ name: trimmedName },
					category.id
				);

				category.name = trimmedName;
				category.isEditing = false;
				category.newSubcategoryName = ""; // Clear subcategory input
				this.getKeywordsCategories();

				this.$vs.notify({
					title: "Succès",
					text: "Catégorie mise à jour avec succès",
					color: "success",
				});
			} catch (error) {
				this.$vs.notify({
					title: "Erreur",
					text: "Échec de la mise à jour de la catégorie",
					color: "danger",
				});
				console.error(error);
			}
		},
		getSubcategoriesForCategory(categoryId) {
			// Add null check to avoid undefined errors
			return (this.subcategories || []).filter(
				(subcategory) => subcategory.category_id === categoryId
			);
		},
	},
	watch: {},
};
</script>

<style scoped>
.keyword-categories-container {
	padding: 20px;
}
.edit-input {
	padding: 5px;
	border: 1px solid #ddd;
	border-radius: 4px;
	width: 100%;
}
.flex-actions {
	display: flex;
	gap: 10px;
}
.action-icon {
	cursor: pointer;
}
.section-title {
	font-size: 1.2rem;
	font-weight: 600;
	margin-bottom: 10px;
}
.subcategories-chips {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-bottom: 10px;
}
.subcategory-chip-wrapper {
	display: flex;
	align-items: center;
	background-color: rgba(0, 0, 0, 0.05);
	border-radius: 20px;
	padding: 2px 8px 2px 2px;
}
.delete-subcategory-icon {
	cursor: pointer;
	margin-left: 4px;
	font-size: 16px;
}
.add-subcategory-inline {
	display: flex;
	align-items: center;
	gap: 8px;
	width: 100%;
}
.subcategory-input {
	flex: 1/2;
	padding: 5px 10px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 0.875rem;
}
.subcategory-management {
	width: 100%;
}
.flex {
	display: flex;
}
.items-center {
	align-items: center;
}
.space-x-5 > * + * {
	margin-left: 1.25rem;
}
.mb-4 {
	margin-bottom: 1rem;
}
.mt-2 {
	margin-top: 0.5rem;
}
</style>